import { useState } from 'react';
import { Search, Store, Plus, MapPin, MoreVertical } from 'lucide-react';
import { usePartner } from '../../../../context/partnerManagement/PartnerContext';
import { usePlanAccess } from '../../../../components/gating/PlanGate';
import EmptyStateTable from '../../../../components/feedback/EmptyStateTable';
import FeatureLocked from '../../../../components/gating/FeatureLocked';
import { partnerData } from '../../utils/partnerHelpers';
import PartnerTable from './sections/PartnerTable/PartnerTable';
import PartnerMobileList from './sections/PartnerMobileList/PartnerMobileList';

const PartnerList = ({ onAddNew, onView, onEdit }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const { partners, isLoading, freezePartner, deletePartner } = usePartner();
  const { isLocked: isPartnerLocked } = usePlanAccess('growth');

  const showEmptyState =
    isPartnerLocked || (partners.length === 0 && !searchQuery && !categoryFilter);

  const filteredPartners = partners.filter((partner) => {
    const matchesSearch =
      partner.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      partner.phone_number?.includes(searchQuery);
    const matchesCategory = categoryFilter ? partner.category === categoryFilter : true;
    return matchesSearch && matchesCategory;
  });

  const handleDelete = (partner) => {
    if (window.confirm('Are you sure you want to delete this partner?')) {
      deletePartner(partner._id, partner.category);
    }
  };

  const handleFreeze = (partner) => {
    freezePartner(partner._id, partner.category);
  };

  return (
    <div className="relative">
      <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-100 shadow-sm overflow-hidden animate-tab-switch min-h-[480px]">
        {/* Header Tabs & Search Bar */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b border-slate-100 flex flex-col xl:flex-row justify-between items-stretch xl:items-center gap-3 sm:gap-4 bg-white">
          {/* Category Tabs */}
          <div className="flex items-center gap-2 sm:gap-4 lg:gap-6 overflow-x-auto no-scrollbar w-full xl:w-auto pb-1 xl:pb-0 scroll-smooth">
            {partnerData.categoryTabs.map((tab) => {
              const isActive = categoryFilter === tab.id;
              const count =
                tab.id === ''
                  ? partners.length
                  : partners.filter((p) => p.category === tab.id).length;

              return (
                <button
                  key={tab.id}
                  onClick={() => setCategoryFilter(tab.id)}
                  className={`flex-shrink-0 min-w-max text-xs sm:text-sm font-bold pb-2 transition-all relative whitespace-nowrap flex items-center gap-1.5 sm:gap-2 ${
                    isActive
                      ? 'text-[#067978] border-b-2 border-[#067978]'
                      : 'text-slate-400 hover:text-slate-700'
                  }`}
                >
                  <span className="hidden sm:inline">{tab.fullLabel}</span>
                  <span className="sm:hidden">{tab.shortLabel}</span>
                  <span
                    className={`text-[10px] sm:text-[11px] px-1.5 sm:px-2 py-0.5 rounded-full font-bold transition-colors ${
                      isActive
                        ? 'bg-[#067978]/10 text-[#067978]'
                        : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search Bar */}
          <div className="relative w-full xl:w-72">
            <input
              type="text"
              placeholder="Search partners..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[#067978]/20 focus:border-[#067978] transition-all text-slate-600"
            />
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Search size={14} />
            </div>
          </div>
        </div>

        {/* Content Area */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-9 h-9 border-[3px] border-slate-200 border-t-[#067978] rounded-full animate-spin"></div>
            <p className="text-xs font-semibold text-slate-400 mt-3 animate-pulse">
              Loading partners...
            </p>
          </div>
        ) : showEmptyState ? (
          <EmptyStateTable
            showOverlayCard={!isPartnerLocked}
            title="No partners yet"
            description="Add your trusted service partners — restaurants, activity guides, and shops. Connect them directly with your travellers on-tour."
            primaryAction={{
              label: 'Add your first partner',
              onClick: onAddNew,
              icon: <Plus size={16} className="shrink-0" />
            }}
            columns={partnerData.partnerColumns}
            rows={partnerData.partnerGhostRows}
            renderRow={(row, index) => (
              <div
                className={`hidden md:grid grid-cols-12 gap-4 items-center px-6 py-3.5 bg-white border-b border-slate-100 ${
                  row.isSample
                    ? 'hover:bg-slate-50/50'
                    : `${row.opacity || 'opacity-30'} select-none pointer-events-none`
                }`}
              >
                <div className="col-span-1 text-sm text-slate-400 font-normal">{index + 1}</div>
                <div className="col-span-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-50 text-[#067978] flex items-center justify-center font-normal text-xs flex-shrink-0">
                      {row.name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-slate-800 text-sm font-normal truncate">{row.name}</div>
                      <div className="flex items-center gap-1 text-[11px] text-slate-400 font-normal mt-0.5">
                        <MapPin size={10} className="text-[#067978] flex-shrink-0" />
                        <span>{row.city}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-span-2">
                  <span className="px-2.5 py-0.5 text-xs font-medium rounded-full capitalize inline-block bg-teal-50 text-[#067978] border border-teal-100/70">
                    {row.category
                      ? row.category.charAt(0).toUpperCase() + row.category.slice(1).toLowerCase()
                      : 'Shop'}
                  </span>
                </div>
                <div className="col-span-3 text-sm font-normal text-slate-600 font-mono">
                  {row.phone}
                </div>
                <div className="col-span-1 flex justify-center">
                  <span className="inline-flex items-center gap-1.5 text-xs font-normal text-emerald-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Active
                  </span>
                </div>
                <div className="col-span-1 flex justify-end">
                  <div className="p-1 rounded-lg text-slate-400 border border-slate-200">
                    <MoreVertical size={14} />
                  </div>
                </div>
              </div>
            )}
            renderMobileRow={(row) => (
              <div
                className={`bg-white border rounded-2xl p-3.5 ${
                  row.isSample
                    ? 'border-slate-200/90 shadow-xs'
                    : 'border-slate-100 opacity-40 select-none pointer-events-none'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-slate-800 text-sm font-normal">{row.name}</h4>
                    <p className="text-[11px] text-slate-400 font-normal">
                      {row.city} ·{' '}
                      {row.category
                        ? row.category.charAt(0).toUpperCase() + row.category.slice(1).toLowerCase()
                        : 'Shop'}
                    </p>
                  </div>
                  <span className="inline-flex items-center text-xs font-normal text-emerald-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5"></span>
                    Active
                  </span>
                </div>
              </div>
            )}
          />
        ) : filteredPartners.length === 0 ? (
          <div className="text-center py-16 bg-slate-50/50 px-4">
            <Store size={48} className="mx-auto text-slate-300 mb-3" />
            <h3 className="text-base font-normal text-slate-700">No Partners Found</h3>
            <p className="text-xs text-slate-400 mt-1 mb-4 font-normal">
              No partners match your selected criteria.
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setCategoryFilter('');
              }}
              className="text-[#067978] font-normal text-xs hover:underline uppercase tracking-wider"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div>
            <PartnerMobileList
              partners={filteredPartners}
              onView={onView}
              onEdit={onEdit}
              onFreeze={handleFreeze}
              onDelete={handleDelete}
            />
            <PartnerTable
              partners={filteredPartners}
              onView={onView}
              onEdit={onEdit}
              onFreeze={handleFreeze}
              onDelete={handleDelete}
            />
          </div>
        )}
      </div>

      {isPartnerLocked && <FeatureLocked featureKey="partners" overlay={true} />}
    </div>
  );
};

export default PartnerList;
